import { useMemo, useState } from 'react';
import { useClientPagination } from '@/hooks/useClientPagination';
import { type ColumnDef } from '@tanstack/react-table';
import { ArrowDownRight, ArrowUpRight, Sparkles } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { KPICard } from '@/components/charts/KPICard';
import { QuadrantChart } from '@/components/charts/QuadrantChart';
import { DataView, downloadCsv } from '@/components/data/DataView';
import { SnapshotMenu } from '@/components/system/SnapshotMenu';
import { TaxonomyRail } from '@/components/inventory/TaxonomyRail';
import { MixDonut } from '@/components/products/MixDonut';
import { ProductDrillDrawer } from '@/components/products/ProductDrillDrawer';
import {
  useInventoryTaxonomy,
  useProductPerformance,
  useProductQuadrant,
  type ProductDrilldownFilters,
  type ProductGroupBy,
  type ProductPerfRow,
} from '@/api/bi';
import { useProductScope } from '@/pages/products/useProductScope';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

interface DimensionDashboardProps {
  eyebrow: string;
  title: string;
  description: string;
  groupBy: ProductGroupBy;
  snapshotName: string;
}

/** Shared focused view for a single grouping dimension (Brands / Categories). */
export function DimensionDashboard({
  eyebrow,
  title,
  description,
  groupBy,
  snapshotName,
}: DimensionDashboardProps) {
  const scope = useProductScope();
  const taxonomy = useInventoryTaxonomy();
  const [drillRow, setDrillRow] = useState<ProductPerfRow | null>(null);
  const returnPath = groupBy === 'brand' ? '/products/brands' : '/products/categories';

  const perf = useProductPerformance({
    start: scope.iso.start,
    end: scope.iso.end,
    compareStart: scope.compareEnabled ? scope.compareRange?.start : undefined,
    compareEnd: scope.compareEnabled ? scope.compareRange?.end : undefined,
    groupBy,
    limit: 300,
    ...scope.taxonomy,
  });
  const quadrant = useProductQuadrant({
    start: scope.iso.start,
    end: scope.iso.end,
    groupBy,
    limit: 80,
    ...scope.taxonomy,
  });

  const rows = perf.data?.rows ?? [];
  const totals = perf.data?.totals;
  const pagination = useClientPagination(25, `${groupBy}-${rows.length}-${scope.iso.start}`);

  const drillFilters = useMemo(
    () => (drillRow ? drillFiltersForDimension(scope, drillRow, groupBy) : null),
    [drillRow, scope, groupBy],
  );

  const columns = useMemo<ColumnDef<ProductPerfRow, unknown>[]>(
    () => [
      { id: 'name', header: title, accessorFn: (r) => r.name, cell: ({ row }) => (
        <span className="truncate text-sm font-medium">{row.original.name}</span>
      ) },
      { id: 'gmv', header: () => <div className="text-right">GMV</div>, accessorFn: (r) => r.gmv, cell: ({ row }) => (
        <div className="kpi-num text-right text-sm font-medium">{fmt.npr(row.original.gmv)}</div>
      ) },
      { id: 'share', header: () => <div className="text-right">Share</div>, accessorFn: (r) => r.contribution_pct, cell: ({ row }) => (
        <div className="kpi-num text-right text-sm">{fmt.pct(row.original.contribution_pct)}</div>
      ) },
      { id: 'units', header: () => <div className="text-right">Units</div>, accessorFn: (r) => r.units, cell: ({ row }) => (
        <div className="kpi-num text-right text-sm">{fmt.number(row.original.units)}</div>
      ) },
      { id: 'margin', header: () => <div className="text-right">Margin %</div>, accessorFn: (r) => r.margin_pct ?? -1, cell: ({ row }) => (
        <div className="kpi-num text-right text-sm">{row.original.margin_pct != null ? fmt.pct(row.original.margin_pct) : '—'}</div>
      ) },
      { id: 'orders', header: () => <div className="text-right">Orders</div>, accessorFn: (r) => r.orders, cell: ({ row }) => (
        <div className="kpi-num text-right text-sm">{fmt.number(row.original.orders)}</div>
      ) },
      { id: 'delta', header: () => <div className="text-right">Δ GMV</div>, accessorFn: (r) => r.gmv_delta_pct ?? 0, cell: ({ row }) => {
        const pct = row.original.gmv_delta_pct;
        if (pct == null) return <div className="text-right text-xs text-muted-foreground">—</div>;
        const positive = pct > 0;
        return (
          <div className={cn('inline-flex w-full items-center justify-end gap-0.5 text-xs font-medium', positive ? 'text-success' : pct < 0 ? 'text-danger' : 'text-muted-foreground')}>
            {positive ? <ArrowUpRight className="h-3 w-3" /> : pct < 0 ? <ArrowDownRight className="h-3 w-3" /> : null}
            {fmt.delta(pct, { unit: 'pct' })}
          </div>
        );
      } },
    ],
    [title],
  );

  return (
    <>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
        actions={
          <div className="flex items-center gap-2">
            <Badge tone="info" className="gap-1"><Sparkles className="h-3 w-3" /> New</Badge>
            <SnapshotMenu filename={snapshotName} />
          </div>
        }
      />

      <TaxonomyRail
        taxonomy={taxonomy.data}
        taxonomyLoading={taxonomy.isLoading}
        vendorIds={scope.vendorIds}
        onVendorIds={scope.setVendorIds}
        brandIds={scope.brandIds}
        onBrandIds={scope.setBrandIds}
        categoryGroupIds={scope.categoryGroupIds}
        onCategoryGroupIds={scope.setCategoryGroupIds}
        categoryIds={scope.categoryIds}
        onCategoryIds={scope.setCategoryIds}
        subcategoryIds={scope.subcategoryIds}
        onSubcategoryIds={scope.setSubcategoryIds}
        q={scope.productQuery}
        onQ={scope.setProductQuery}
        onClear={scope.hasSelection ? scope.clear : undefined}
      />

      <div className="grid gap-4 stagger sm:grid-cols-2 lg:grid-cols-4">
        <KPICard label="GMV" value={fmt.npr(totals?.gmv ?? 0)} loading={perf.isLoading} />
        <KPICard label="Units" value={fmt.compact(totals?.units ?? 0)} loading={perf.isLoading} />
        <KPICard label="Gross margin" value={totals?.margin_pct != null ? fmt.pct(totals.margin_pct) : '—'} loading={perf.isLoading} />
        <KPICard label={`Active ${title.toLowerCase()}`} value={fmt.number(totals?.groups ?? 0)} loading={perf.isLoading} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Revenue mix</CardTitle></CardHeader>
          <CardBody>
            {perf.isLoading ? <Skeleton className="h-[220px] w-full" /> : <MixDonut rows={rows} />}
          </CardBody>
        </Card>
        <Card>
          <CardHeader><CardTitle>Profitability triage</CardTitle></CardHeader>
          <CardBody>
            {quadrant.isLoading ? (
              <Skeleton className="h-[320px] w-full" />
            ) : quadrant.isError ? (
              <ErrorState onRetry={() => void quadrant.refetch()} />
            ) : (
              <QuadrantChart
                points={quadrant.data?.points ?? []}
                formatNpr={(n) => fmt.npr(n)}
                xLabel="GMV share"
                yLabel="Margin Δ vs median"
                zLabel="Gross profit"
                onPointClick={(p) => setDrillRow({ id: p.id, name: p.label } as ProductPerfRow)}
              />
            )}
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        <DataView<ProductPerfRow>
          title={`${title} performance`}
          data={rows}
          columns={columns}
          isLoading={perf.isLoading}
          isError={perf.isError}
          onRetry={() => void perf.refetch()}
          clientPagination
          pagination={{ ...pagination, total: rows.length }}
          onRowClick={(row) => setDrillRow(row)}
          onExportCsv={() =>
            downloadCsv(
              snapshotName,
              rows.map((r) => ({
                name: r.name,
                gmv: r.gmv,
                units: r.units,
                margin_pct: r.margin_pct ?? '',
                orders: r.orders,
                share: r.contribution_pct,
                gmv_delta_pct: r.gmv_delta_pct ?? '',
              })),
              [
                { key: 'name', label: title },
                { key: 'gmv', label: 'GMV' },
                { key: 'units', label: 'Units' },
                { key: 'margin_pct', label: 'Margin %' },
                { key: 'orders', label: 'Orders' },
                { key: 'share', label: 'Share' },
                { key: 'gmv_delta_pct', label: 'GMV Δ' },
              ],
            )
          }
          emptyTitle="No sales in range"
        />
      </div>
      {drillFilters && drillRow ? (
        <ProductDrillDrawer
          open={Boolean(drillRow)}
          onOpenChange={(open) => { if (!open) setDrillRow(null); }}
          title={`Orders & customers · ${drillRow.name}`}
          returnPath={returnPath}
          filters={drillFilters}
        />
      ) : null}
    </>
  );
}

function drillFiltersForDimension(
  scope: ReturnType<typeof useProductScope>,
  row: ProductPerfRow,
  groupBy: ProductGroupBy,
): ProductDrilldownFilters {
  const base: ProductDrilldownFilters = {
    start: scope.iso.start,
    end: scope.iso.end,
    storeId: scope.storeId,
    storeIds: scope.storeIds,
    vendorIds: scope.vendorIds,
    brandIds: scope.brandIds,
    categoryGroupIds: scope.categoryGroupIds,
    categoryIds: scope.categoryIds,
    subcategoryIds: scope.subcategoryIds,
    q: scope.productQuery.trim() || undefined,
  };
  if (groupBy === 'brand') return { ...base, brandIds: [row.id] };
  if (groupBy === 'category_group') return { ...base, categoryGroupIds: [row.id] };
  if (groupBy === 'category') return { ...base, categoryIds: [row.id] };
  if (groupBy === 'subcategory') return { ...base, subcategoryIds: [row.id] };
  return { ...base, productIds: [row.id] };
}
